import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { passwordProblem, type SessionUser } from "@devengo/shared";

import { Button, ErrorBox, Field, Input } from "../components/ui";
import { api } from "../lib/api";
import { AuthFrame, useSignedIn } from "./login";

/**
 * Where the mailed links land (`/acceso#<token>`): accept an invitation or choose a new password
 * after «Olvidé mi clave». The token travels in the fragment, so it never reaches a server log.
 */
export function AccessPage() {
  const signedIn = useSignedIn();
  const [token] = useState(() => window.location.hash.replace(/^#/, ""));
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const info = useQuery({
    queryKey: ["access", token],
    queryFn: () =>
      api.post<{ purpose: "invitation" | "password_reset"; name: string; email: string }>(
        "/api/auth/access/info",
        { token },
      ),
    enabled: token.length > 0,
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () => api.post<SessionUser>("/api/auth/access/accept", { token, password }),
    onSuccess: (user) => {
      window.history.replaceState(null, "", "/");
      signedIn(user);
    },
  });
  const problem = password.length > 0 ? passwordProblem(password, { name: info.data?.name }) : null;
  const mismatch = repeat.length > 0 && repeat !== password;

  if (!token) {
    return (
      <AuthFrame>
        <p className="text-sm">Este enlace está incompleto. Abrilo de nuevo desde el mail.</p>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      {info.isLoading ? <p className="text-sm text-brand-muted">Revisando el enlace…</p> : null}
      <ErrorBox error={info.error} />
      {info.data ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            accept.mutate();
          }}
        >
          <p className="text-sm">
            {info.data.purpose === "invitation"
              ? `Hola, ${info.data.name}. Elegí la clave con la que vas a ingresar (${info.data.email}).`
              : `Hola, ${info.data.name}. Elegí tu clave nueva. Se van a cerrar tus otras sesiones.`}
          </p>
          <Field
            label="Clave nueva"
            error={problem ?? undefined}
            hint="Al menos 10 caracteres. Una frase que recuerdes sirve: «mate amargo en la terraza»."
          >
            <Input
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field label="Repetila" error={mismatch ? "No coincide con la anterior." : undefined}>
            <Input
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </Field>
          <ErrorBox error={accept.error} />
          <Button
            type="submit"
            disabled={!password || problem !== null || repeat !== password || accept.isPending}
          >
            {info.data.purpose === "invitation"
              ? "Elegir clave e ingresar"
              : "Cambiar clave e ingresar"}
          </Button>
        </form>
      ) : null}
    </AuthFrame>
  );
}
